/**
 * refund-alerts 域 Zod 共享契约 — 退款异常告警（ADM-FIN-006）
 *
 * 来源：高保真原型 data.jsx REFUND_ALERT_RULES / REFUND_ALERT_LOG，契约对齐 dev-docs 提案
 * 2026-05-31-admin-gap-domains-contract.md ④。所有闭集枚举 + 金额/阈值字段命名与 snake_case 对齐。
 *
 * 阈值口径：率类指标（passive_refund_rate / dispute_rate，unit=percent）阈值/实测值以
 * 「基点 basis points」整数表示（3.0% → 300，2 位小数精度）；金额绝对值指标
 * （refund_amount_absolute，unit=货币码）以 minor units 表示（5000 EUR → 500000）。
 * 阈值 / 实测值字段一律 bigint，API 序列化为 string。
 */

import { z } from 'zod';

// ─────────────────────────── 闭集枚举 ───────────────────────────

/** 监控范围闭集（原型 全平台 / Apple IAP / Stripe；paypal 占位与渠道契约对齐） */
export const REFUND_ALERT_SCOPES = ['all', 'apple_iap', 'stripe', 'paypal'] as const;
export type RefundAlertScope = (typeof REFUND_ALERT_SCOPES)[number];

/** 监控指标闭集（原型 被动退款率 / 退款金额绝对值 / 争议申诉率） */
export const REFUND_ALERT_METRICS = [
  'passive_refund_rate',
  'refund_amount_absolute',
  'dispute_rate',
] as const;
export type RefundAlertMetric = (typeof REFUND_ALERT_METRICS)[number];

/** 统计时间窗闭集（原型 24h / 1h / 7d） */
export const REFUND_ALERT_WINDOWS = ['1h', '24h', '7d', '30d'] as const;
export type RefundAlertWindow = (typeof REFUND_ALERT_WINDOWS)[number];

/** 通知通道闭集（原型 邮件 / Slack #fin-alert / SMS） */
export const REFUND_ALERT_CHANNELS = ['email', 'slack', 'sms'] as const;
export type RefundAlertChannel = (typeof REFUND_ALERT_CHANNELS)[number];

/** 告警级别闭集（原型 warn / critical） */
export const REFUND_ALERT_LEVELS = ['warn', 'critical'] as const;
export type RefundAlertLevel = (typeof REFUND_ALERT_LEVELS)[number];

const ScopeSchema = z.enum(REFUND_ALERT_SCOPES);
const MetricSchema = z.enum(REFUND_ALERT_METRICS);
const WindowSchema = z.enum(REFUND_ALERT_WINDOWS);
const ChannelSchema = z.enum(REFUND_ALERT_CHANNELS);
/** 阈值 / 实测值字符串：正整数（基点 bps 或 minor units） */
const PositiveIntStringSchema = z
  .string()
  .regex(/^[1-9][0-9]*$/, 'must be positive integer string');
/** 单位：percent 或 ISO 4217 货币码（金额绝对值指标用，如 EUR） */
const UnitSchema = z.union([z.literal('percent'), z.string().regex(/^[A-Z]{3}$/, 'ISO 4217 code')]);

// ─────────────────────────── 规则 CRUD 请求 ───────────────────────────

export const CreateRefundAlertRuleRequestSchema = z.object({
  scope: ScopeSchema,
  metric: MetricSchema,
  threshold: PositiveIntStringSchema,
  unit: UnitSchema,
  window: WindowSchema,
  channels: z.array(ChannelSchema).min(1, 'at least one channel'),
  enabled: z.boolean().optional(),
});
export type CreateRefundAlertRuleRequest = z.infer<typeof CreateRefundAlertRuleRequestSchema>;

/** PATCH 规则：全部可选（含 enabled toggle）；至少改一个字段 */
export const UpdateRefundAlertRuleRequestSchema = z
  .object({
    scope: ScopeSchema.optional(),
    metric: MetricSchema.optional(),
    threshold: PositiveIntStringSchema.optional(),
    unit: UnitSchema.optional(),
    window: WindowSchema.optional(),
    channels: z.array(ChannelSchema).min(1).optional(),
    enabled: z.boolean().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: 'at least one field to update' });
export type UpdateRefundAlertRuleRequest = z.infer<typeof UpdateRefundAlertRuleRequestSchema>;

// ─────────────────────────── 响应 DTO ───────────────────────────

export type RefundAlertRuleResponse = {
  id: string;
  scope: RefundAlertScope;
  metric: RefundAlertMetric;
  /** 基点 bps 或 minor units（与 unit 同语义），string */
  threshold: string;
  /** percent 或 ISO 4217 货币码 */
  unit: string;
  window: RefundAlertWindow;
  channels: RefundAlertChannel[];
  enabled: boolean;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type RefundAlertEventResponse = {
  id: string;
  triggered_at: string;
  rule_id: string | null;
  scope: RefundAlertScope;
  metric: RefundAlertMetric;
  /** 实测值（基点 bps 或 minor units），string */
  observed_value: string;
  /** 触发时阈值快照，string */
  threshold: string;
  unit: string;
  app_id: string | null;
  level: RefundAlertLevel;
  acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
};
